import { computed, inject, Injectable } from '@angular/core';
import { UnityPermissionType } from '@unity/clients';
import { UnityEntitlementPath, UnityPermissionTypeStrings } from '@unity/utils/types';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly sessionService = inject(SessionService);

  private readonly $_currentPermissionFlags = computed(() => this.sessionService.$currentAgencyRole()?.flags);

  private decomposePermissionType = (maskValue: number): UnityPermissionTypeStrings[] =>
    (Object.values(UnityPermissionType) as (string | number)[])
      .filter(v => typeof v === 'number' && maskValue & v)
      .map(n => UnityPermissionType[n as number] as UnityPermissionTypeStrings)
      .filter(k => ![UnityPermissionType[UnityPermissionType.All], UnityPermissionType[UnityPermissionType.None]].includes(k));

  private readonly $_filtered = computed(() => {
    const entitlements = this.$_currentPermissionFlags();
    if (!entitlements) {
      return [];
    }
    return Object.entries(entitlements).flatMap(([entitlement, permissionMaskValue]) => {
      const validPerms = this.decomposePermissionType(permissionMaskValue).filter(
        k => ![UnityPermissionType[UnityPermissionType.All], UnityPermissionType[UnityPermissionType.None]].includes(k)
      );
      return validPerms.map(permission => `${entitlement}:${permission}` as UnityEntitlementPath);
    });
  });

  public readonly $currentPermissions = computed(() => {
    const loggedIn = this.sessionService.$isLoggedIn();
    const filtered = this.$_filtered();
    if (!loggedIn) {
      return [];
    }
    return filtered;
  });

  public userIsEntitled(path: UnityEntitlementPath) {
    return this.$currentPermissions().includes(path);
  }

  public userIsEntitledToOneOf(paths: UnityEntitlementPath[]) {
    for (const path of paths) {
      if (this.userIsEntitled(path)) {
        return true;
      }
    }
    return false;
  }
}
